import {
  FormControl,
  FormControlLabel,
  FormHelperText,
  DialogContent,
  Radio,
  RadioGroup,
  Typography,
  makeStyles,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import type { WorkerPresentation } from '../hooks/useWorkerStatus';
import type { PortalActivityWorkPath } from './contracts';
import type { ActivityWorkPathOption } from './useActivityWorkPathOptions';

/**
 * Apresentação do diálogo de escolha da via. Separado do componente que governa
 * abertura, seleção e confirmação para manter cada arquivo e cada função dentro
 * dos limites do repositório.
 */
const useStyles = makeStyles(theme => ({
  option: {
    alignItems: 'flex-start',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    margin: theme.spacing(0, 0, 1.5),
    padding: theme.spacing(1.5),
  },
  details: {
    display: 'block',
    marginTop: theme.spacing(0.5),
  },
  prerequisites: {
    margin: theme.spacing(0.5, 0, 0),
    paddingLeft: theme.spacing(2.5),
  },
  worker: {
    marginBottom: theme.spacing(2),
  },
}));

export interface ActivityJourneyLauncherProps {
  open: boolean;
  onClose: () => void;
}

export const WORKER_LABELS: Record<WorkerPresentation, string> = {
  loading: 'Consultando o estado do Frontier Worker...',
  connected: 'Frontier Worker conectado',
  disconnected: 'Frontier Worker não conectado',
  unavailable:
    'Estado do Frontier Worker indisponível. Não foi possível determinar o estado; isso não significa que o worker está desconectado.',
};

export const WORKER_INVARIANT_TEXT =
  'O estado do worker é apenas informativo e não altera a via selecionada.';

export const ROUTE_FAILURE_TEXT =
  'Não foi possível abrir a tela de criação desta via neste portal.';

export const NO_CREATABLE_WORK_PATH_TEXT =
  'Nenhuma via de criação está disponível neste portal.';

export const UNBOUND_ROUTE_HELPER_TEXT =
  'A rota Control Plane ainda não está vinculada neste app.';

const PrerequisiteList = ({ items }: { items: readonly string[] }) => {
  const classes = useStyles();
  return (
    <>
      <Typography
        component="span"
        variant="caption"
        className={classes.details}
      >
        Pré-requisitos:
      </Typography>
      <ul className={classes.prerequisites}>
        {items.map(item => (
          <Typography key={item} component="li" variant="caption">
            {item}
          </Typography>
        ))}
      </ul>
    </>
  );
};

const JourneyOptionDetails = ({
  copy,
  id,
}: {
  copy: ActivityWorkPathOption['copy'];
  id: string;
}) => {
  const classes = useStyles();
  return (
    <div id={id}>
      <Typography
        component="span"
        variant="body2"
        color="textSecondary"
        className={classes.details}
      >
        {copy.description}
      </Typography>
      <PrerequisiteList items={copy.prerequisites} />
      <Typography
        component="span"
        variant="caption"
        className={classes.details}
      >
        {copy.consequence}
      </Typography>
    </div>
  );
};

const JourneyOption = ({
  option,
  describedById,
}: {
  option: ActivityWorkPathOption;
  describedById: string;
}) => {
  const classes = useStyles();
  return (
    <div className={classes.option}>
      <FormControlLabel
        value={option.workPath}
        control={
          <Radio
            color="primary"
            inputProps={{ 'aria-describedby': describedById }}
          />
        }
        // Nome curto: só o título da via. O detalhamento fica fora do rótulo,
        // associado por `aria-describedby`, para que o leitor de tela não
        // anuncie um parágrafo inteiro como nome do controle (F-06).
        label={
          <Typography component="span" variant="subtitle1">
            {option.copy.title}
          </Typography>
        }
      />
      <JourneyOptionDetails copy={option.copy} id={describedById} />
    </div>
  );
};

/**
 * Única região viva do diálogo. `role="status"` sobrescreve o `role="alert"`
 * padrão do Alert v4, que é assertivo: somar uma segunda região polida
 * produziria anúncio duplicado, o oposto do que RN-16 pede.
 */
const WorkerStateAlert = ({
  presentation,
}: {
  presentation: WorkerPresentation;
}) => {
  const classes = useStyles();
  return (
    <Alert
      severity="info"
      role="status"
      aria-live="polite"
      aria-atomic="true"
      className={classes.worker}
    >
      {`${WORKER_LABELS[presentation]} ${WORKER_INVARIANT_TEXT}`}
    </Alert>
  );
};

const JourneyChoices = ({
  options,
  selected,
  onSelect,
  helperId,
  showHelper,
}: {
  options: readonly ActivityWorkPathOption[];
  selected: PortalActivityWorkPath | undefined;
  onSelect: (workPath: PortalActivityWorkPath) => void;
  helperId: string;
  showHelper: boolean;
}) => (
  <FormControl component="fieldset" fullWidth>
    <RadioGroup
      aria-label="Via de Trabalho"
      value={selected ?? ''}
      onChange={event => onSelect(event.target.value as PortalActivityWorkPath)}
    >
      {options.map(option => (
        <JourneyOption
          key={option.workPath}
          option={option}
          describedById={`${option.workPath}-details`}
        />
      ))}
    </RadioGroup>
    {showHelper && (
      <FormHelperText id={helperId}>{UNBOUND_ROUTE_HELPER_TEXT}</FormHelperText>
    )}
  </FormControl>
);

/**
 * Corpo do diálogo. Montado somente enquanto aberto, e é isso que garante estado
 * limpo por abertura: seleção na via padrão do contrato e worker em
 * "consultando", sem resíduo da abertura anterior (ADR-003). Reiniciar por
 * efeito de limpeza seria mais frágil que não existir.
 */
export const JourneyDialogContent = ({
  presentation,
  routeFailure,
  options,
  selected,
  onSelect,
  helperId,
  showHelper,
}: {
  presentation: WorkerPresentation;
  routeFailure: boolean;
  options: readonly ActivityWorkPathOption[];
  selected: PortalActivityWorkPath | undefined;
  onSelect: (workPath: PortalActivityWorkPath) => void;
  helperId: string;
  showHelper: boolean;
}) => {
  const classes = useStyles();
  return (
    <DialogContent>
      <WorkerStateAlert presentation={presentation} />
      {routeFailure && (
        <Alert severity="error" className={classes.worker}>
          {ROUTE_FAILURE_TEXT}
        </Alert>
      )}
      {options.length === 0 ? (
        <Typography variant="body2">{NO_CREATABLE_WORK_PATH_TEXT}</Typography>
      ) : (
        <JourneyChoices
          options={options}
          selected={selected}
          onSelect={onSelect}
          helperId={helperId}
          showHelper={showHelper}
        />
      )}
    </DialogContent>
  );
};
